/**
 * Retries thumbnails that fail to load.
 *
 * Thumbnails the server hasn't made yet are served through the image-resizer
 * function (`/api/resize-image`). When a burst of them arrives (a large
 * attachment grid), the function adds instances, and a new instance can
 * briefly restart; requests that land in that gap fail (503) and the browser
 * shows a broken image. A later load succeeds, so this reloads a failed
 * resizer thumbnail a few times with growing pauses instead of leaving it
 * broken until the user refreshes.
 *
 * One capture-phase listener on document sees load errors from every <img>
 * (React or legacy), since image `error` events do not bubble. Only resizer
 * links are retried; the extra query parameter makes the browser fetch again
 * and is ignored by the function. Self-attaching on import.
 */

export const RETRY_DELAYS_MS = [3_000, 10_000, 30_000];
export const RETRY_PARAM = 'thumbRetry';
const ATTEMPT_ATTR = 'data-thumb-retry';

export function isResizerUrl(src: string): boolean {
  try {
    return new URL(src, window.location.href).pathname.endsWith('/api/resize-image');
  } catch {
    return false;
  }
}

/** The same link with the retry counter set, so the browser does not reuse the failed response. */
export function withRetryParam(src: string, attempt: number): string {
  const url = new URL(src, window.location.href);
  url.searchParams.set(RETRY_PARAM, String(attempt));
  return url.toString();
}

function onImageError(event: Event): void {
  const img = event.target;
  if (!(img instanceof HTMLImageElement)) return;
  const src = img.currentSrc || img.src;
  if (!src || !isResizerUrl(src)) return;

  const attempt = Number(img.getAttribute(ATTEMPT_ATTR) || '0') + 1;
  if (attempt > RETRY_DELAYS_MS.length) return;
  img.setAttribute(ATTEMPT_ATTR, String(attempt));

  window.setTimeout(() => {
    // Skip if the image left the page or something else gave it a new source meanwhile.
    if (!img.isConnected || (img.currentSrc || img.src) !== src) return;
    img.src = withRetryParam(src, attempt);
  }, RETRY_DELAYS_MS[attempt - 1]);
}

let installed = false;

export function installThumbnailRetry(): void {
  if (installed) return;
  installed = true;
  document.addEventListener('error', onImageError, true);
}

installThumbnailRetry();
